import React from 'react';
import './Skeleton.css';

/**
 * Componente Skeleton Loader reutilizable
 * @param {Object} props
 * @param {string} props.variant - Tipo de skeleton: 'text', 'title', 'circular', 'rectangular', 'card'
 * @param {string} props.width - Ancho personalizado (ej: '100%', '200px')
 * @param {string} props.height - Alto personalizado (ej: '20px', '100px')
 * @param {number} props.count - Número de líneas (solo para variant='text')
 * @param {string} props.className - Clases CSS adicionales
 * @param {Object} props.style - Estilos inline adicionales
 */
const Skeleton = ({ 
  variant = 'text', 
  width, 
  height, 
  count = 1,
  className = '',
  style = {} 
}) => {
  
  // Estilos base según variante
  const getVariantStyle = () => {
    const baseStyle = {
      width: width || '100%',
      height: height,
    };

    switch (variant) {
      case 'text':
        return {
          ...baseStyle,
          height: height || '16px',
          marginBottom: '8px',
        };
      
      case 'title':
        return {
          ...baseStyle,
          height: height || '28px',
          marginBottom: '12px',
        };
      
      case 'circular':
        const size = width || height || '40px';
        return {
          width: size,
          height: size,
          borderRadius: '50%',
        };
      
      case 'rectangular':
        return {
          ...baseStyle,
          height: height || '200px',
          borderRadius: '8px',
        };
      
      case 'card':
        return {
          ...baseStyle,
          height: height || '300px',
          borderRadius: '12px',
        };
      
      default:
        return baseStyle;
    }
  };

  const skeletonStyle = {
    ...getVariantStyle(),
    ...style,
  };

  // Renderizar múltiples líneas si count > 1
  if (variant === 'text' && count > 1) {
    return (
      <div className="skeleton-group">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={`skeleton skeleton-${variant} ${className}`}
            style={skeletonStyle}
          />
        ))}
      </div>
    );
  }

  // Renderizar skeleton único
  return (
    <div
      className={`skeleton skeleton-${variant} ${className}`}
      style={skeletonStyle}
    />
  );
};

/**
 * Skeleton predefinido para tabla
 */
export const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="skeleton-table">
      {/* Header */}
      <div className="skeleton-table-header">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={`header-${index}`} variant="text" width="100%" height="20px" />
        ))}
      </div>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="skeleton-table-row">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton 
              key={`cell-${rowIndex}-${colIndex}`} 
              variant="text" 
              width="100%" 
              height="16px" 
            />
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * Skeleton predefinido para tarjeta (card)
 */
export const SkeletonCard = () => {
  return (
    <div className="skeleton-card-wrapper">
      <Skeleton variant="rectangular" height="150px" />
      <div style={{ padding: '16px' }}>
        <Skeleton variant="title" width="60%" />
        <Skeleton variant="text" count={3} />
      </div>
    </div>
  );
};

/**
 * Skeleton predefinido para perfil de usuario
 */
export const SkeletonProfile = () => {
  return (
    <div className="skeleton-profile">
      <Skeleton variant="circular" width="80px" height="80px" />
      <div className="skeleton-profile-info">
        <Skeleton variant="title" width="200px" />
        <Skeleton variant="text" width="150px" />
        <Skeleton variant="text" count={2} width="100%" />
      </div>
    </div>
  );
};

/**
 * Skeleton predefinido para lista
 */
export const SkeletonList = ({ items = 5 }) => {
  return (
    <div className="skeleton-list">
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="skeleton-list-item">
          <Skeleton variant="circular" width="40px" height="40px" />
          <div className="skeleton-list-content">
            <Skeleton variant="text" width="70%" height="18px" />
            <Skeleton variant="text" width="40%" height="14px" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default React.memo(Skeleton);
